import type { MerchantApiClient } from "../../types.js";
import { dispatchMerchantTelemetry } from "../../shared/telemetry.js";
import { createMerchantDashboardApi, type MerchantDashboardApi } from "./api.js";
import type { MerchantDashboardDto, MerchantDashboardFilters } from "./models.js";

export interface MerchantDashboardTelemetry {
  track(eventName: string, payload: Record<string, string | number | boolean | undefined>): void;
}

export interface MerchantDashboardService {
  loadDashboard(filters: MerchantDashboardFilters): Promise<MerchantDashboardDto>;
}

export interface MerchantDashboardServiceOptions {
  api?: MerchantDashboardApi;
  apiClient?: MerchantApiClient;
  telemetry?: MerchantDashboardTelemetry;
}

export function createMerchantDashboardService(
  options: MerchantDashboardServiceOptions,
): MerchantDashboardService {
  const api = options.api ?? createApiFromClient(options.apiClient);
  const telemetry = options.telemetry ?? browserTelemetry();

  return {
    async loadDashboard(filters) {
      const startedAt = performance.now();
      try {
        const dashboard = await api.getDashboard(filters);
        telemetry.track("merchant_dashboard_loaded", {
          result: "success",
          durationMs: Math.round(performance.now() - startedAt),
          kpiCards: dashboard.kpiCards.length,
          needsAttention: dashboard.needsAttention.total,
        });
        return dashboard;
      } catch (error: unknown) {
        telemetry.track("merchant_dashboard_loaded", {
          result: "failure",
          durationMs: Math.round(performance.now() - startedAt),
          errorName: error instanceof Error ? error.name : "unknown",
        });
        throw error;
      }
    },
  };
}

function createApiFromClient(client: MerchantApiClient | undefined): MerchantDashboardApi {
  if (client === undefined) {
    throw new Error("Merchant dashboard service requires an API client.");
  }

  return createMerchantDashboardApi(client);
}

function browserTelemetry(): MerchantDashboardTelemetry {
  return {
    track(eventName, payload) {
      dispatchMerchantTelemetry(eventName, payload);
    },
  };
}
